import React, { useState, useEffect, useRef } from 'react';
import useSidebarResizing from '../hooks/useSidebarResizing';
import { useDispatch, useSelector } from 'react-redux';
import DjChangeBanner from './DjChangeBanner';
import { wrappablePath } from '../utils/formatPath';
import { formatDjImportSummary, formatDjImportStopped } from '../utils/djImportSummary';
import SendToAppModal from './SendToAppModal';
import SyncFromAppModal from './SyncFromAppModal';
import AutoSyncToggle from './AutoSyncToggle';
import DjLibraryMeter from './DjLibraryMeter';
import './DjLocationControls.scss';
import useAutoSyncNudge from '../hooks/useAutoSyncNudge';
import rekordboxLogo from '../assets/rekordbox-logo.png';
import { getImportExclusions, localPlaylistSelectionKey } from '../utils/djImportSelection';
import { persistDjSidebarWidth, DJ_SIDEBAR_MIN_WIDTH, DJ_SIDEBAR_MAX_WIDTH } from '../redux/utils/djSidebarWidth';
import { track } from '../utils/analytics';
import { clearSyncRequest } from '../redux/slices/djLibraryChangesSlice';
import { Button, Spinner, ListGroup, Badge } from 'react-bootstrap';
import ErrorBanner from './ErrorBanner';
import {
    loadPersistedRekordboxState,
    toggleRekordboxSidebar,
    setRekordboxSidebarWidth,
    setSyncStatus,
    setRekordboxPath,
    setRekordboxExists,
    setRekordboxPlaylists,
    setLastSyncTime,
    setImporting,
    setExporting,
    setRekordboxError
} from '../redux/slices/rekordboxSlice';
import { triggerPlaylistRefresh } from '../redux/slices/playlistSlice';
import { useToast } from '../contexts/ToastContext';
import './RekordboxSidebar.scss';
import { errMsg } from '../redux/utils/errMsg';

const RekordboxSidebar = () => {
    const dispatch = useDispatch();
    const toast = useToast();
    const {
        sidebarOpen,
        sidebarWidth,
        syncStatus,
        rekordboxPath,
        rekordboxExists,
        rekordboxPlaylists,
        lastSyncTime,
        importing,
        exporting,
        error
    } = useSelector(state => state.rekordbox);

    const authUserId = useSelector((state) => state.auth?.user?.id) || null;
    const maybeNudgeAutoSync = useAutoSyncNudge('rekordbox', { label: 'Rekordbox', logo: rekordboxLogo });
    const [isResizing, setIsResizing] = useState(false);
    useSidebarResizing(isResizing);
    const sidebarRef = useRef(null);
    const widthRef = useRef(sidebarWidth);
    widthRef.current = sidebarWidth;
    const [selectedPlaylistsForExport, setSelectedPlaylistsForExport] = useState([]);
    const [playlistsLoaded, setPlaylistsLoaded] = useState(false);

    const [hasCheckedRekordbox, setHasCheckedRekordbox] = useState(false);

    // Load persisted state on mount
    useEffect(() => {
        const loadAsync = async () => {
            try {
                // "Last synced" is not read here: it belongs to the account and is
                // hydrated from the main process by DjSyncStateHydrator.
                const rekordboxPath = localStorage.getItem('rekordbox.path') || null;
                const rekordboxPlaylists = JSON.parse(localStorage.getItem('rekordbox.playlists')) || [];

                dispatch(loadPersistedRekordboxState({
                    rekordboxPath,
                    rekordboxPlaylists
                }));
            } catch (e) {
                console.error('Failed to load persisted rekordbox state:', e);
                dispatch(loadPersistedRekordboxState({}));
            }
        };
        loadAsync();
    }, [dispatch]);

    // Save rekordbox state on app close - avoids blocking during usage
    useEffect(() => {
        const saveOnClose = () => {
            try {
                localStorage.setItem('rekordbox.path', rekordboxPath);
                localStorage.setItem('rekordbox.playlists', JSON.stringify(rekordboxPlaylists));
            } catch (e) {
                // Silently fail
            }
        };

        window.addEventListener('beforeunload', saveOnClose);
        return () => window.removeEventListener('beforeunload', saveOnClose);
    }, [rekordboxPath, rekordboxPlaylists]);

    // Load Rekordbox-imported playlists from database (with caching)
    const loadImportedPlaylists = async (forceRefresh = false) => {
        // Use cached data if available and not forcing refresh
        if (!forceRefresh && rekordboxPlaylists.length > 0) {
            return rekordboxPlaylists;
        }

        try {
            // Pass `false` — the sidebar only renders playlist name + trackCount
            // (the backend returns trackCount either way). Asking for tracks
            // here would IPC every track in every playlist, freezing the
            // renderer on large libraries.
            const allPlaylists = await window.electron.db.playlists.getAll(false);
            // Filter to only show playlists imported from Rekordbox
            const rekordboxImported = allPlaylists.filter(p =>
                (p.apps && p.apps.includes('rekordbox')) || (p.description && p.description.includes('Imported from Rekordbox'))
            );
            dispatch(setRekordboxPlaylists(rekordboxImported));
            setPlaylistsLoaded(true);
            return rekordboxImported;
        } catch (err) {
            console.error('[RekordboxSidebar] Error loading imported playlists:', err);
            return [];
        }
    };

    // On sidebar open: use cached data, only fetch if needed
    useEffect(() => {
        if (!sidebarOpen) return;

        // If we have cached playlists, use them immediately
        if (rekordboxPlaylists.length > 0) {
            if (syncStatus !== 'partial') dispatch(setSyncStatus('synced'));

            // Still check Rekordbox XML once per session (fast operation)
            if (!hasCheckedRekordbox) {
                checkRekordboxXml();
                setHasCheckedRekordbox(true);
            }
            return;
        }

        // No cached data - need to load from database
        const initialize = async () => {
            if (!hasCheckedRekordbox) {
                dispatch(setSyncStatus('checking'));
            }

            const playlists = await loadImportedPlaylists();

            if (!hasCheckedRekordbox) {
                await checkRekordboxXml();
                setHasCheckedRekordbox(true);
            }

            if (syncStatus !== 'partial') dispatch(setSyncStatus(playlists.length > 0 ? 'synced' : 'not_synced'));
        };

        initialize();
    }, [sidebarOpen]);

    // Handle sidebar resize
    useEffect(() => {
        if (!isResizing) return;

        const handleMouseMove = (e) => {
            // The sidebar sits right of the icon rail (position:fixed with a
            // left offset) — width is cursor minus its left edge, not raw clientX.
            const left = sidebarRef.current?.getBoundingClientRect().left ?? 0;
            const newWidth = e.clientX - left;
            if (newWidth >= DJ_SIDEBAR_MIN_WIDTH && newWidth <= DJ_SIDEBAR_MAX_WIDTH) {
                dispatch(setRekordboxSidebarWidth(newWidth));
            }
        };

        const handleMouseUp = () => {
            setIsResizing(false);
            persistDjSidebarWidth(widthRef.current);
        };

        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);

        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, [isResizing, dispatch]);

    const [rekordboxType, setRekordboxType] = useState(null);
    const [rekordboxVersion, setRekordboxVersion] = useState(null);

    const checkRekordboxXml = async () => {
        try {
            const result = await window.electron.rekordbox.checkXmlExists();
            dispatch(setRekordboxPath(result.path));
            dispatch(setRekordboxExists(result.exists));
            setRekordboxType(result.type);
            setRekordboxVersion(result.version);
            return !!result.exists;
        } catch (err) {
            console.error('Error checking Rekordbox:', err);
            dispatch(setRekordboxError(err.message));
            return false;
        }
    };

    // Re-scan must SAY what it found — a re-check that ends in the same
    // "not found" card changes nothing on screen and reads as a dead button.
    const rescanRekordbox = async () => {
        const found = await checkRekordboxXml();
        if (found) toast.success('Rekordbox library found');
        else toast.info('Still no Rekordbox library in the usual locations.');
    };

    const handleSelectXmlFile = async () => {
        try {
            const result = await window.electron.dialog.showOpenDialog({
                title: 'Select Rekordbox XML file',
                filters: [{ name: 'XML Files', extensions: ['xml'] }],
                properties: ['openFile']
            });

            if (!result.canceled && result.filePaths.length > 0) {
                const filePath = result.filePaths[0];
                dispatch(setRekordboxPath(filePath));
                dispatch(setRekordboxExists(true));
            }
        } catch (err) {
            console.error('Error selecting XML file:', err);
            toast.error('Failed to select XML file');
        }
    };

    // opts.playlistKeys — explicit picker selection (SyncFromAppModal).
    // Without it, the persisted exclusions apply, so one-click re-syncs
    // never re-import playlists the user unchecked.
    const handleImport = async (opts = {}) => {
        const playlistKeys = Array.isArray(opts.playlistKeys) ? opts.playlistKeys : null;
        const excludeKeys = playlistKeys ? [] : getImportExclusions('rekordbox', authUserId);
        const wasFirstSync = !lastSyncTime; // captured before setLastSyncTime below
        dispatch(setImporting(true));
        const importStartedAt = Date.now();
        try {
            const result = await window.electron.rekordbox.importPlaylists(rekordboxPath, {
                playlistKeys: playlistKeys || undefined,
                excludeKeys: excludeKeys.length ? excludeKeys : undefined,
            });

            // Stopped from the picker: playlists imported before the stop are
            // COMPLETE (each commits together with its tracks); the rest —
            // and the sync-complete mark — wait for the next run.
            if (result?.canceled) {
                dispatch(triggerPlaylistRefresh());
                const kept = await loadImportedPlaylists(true);
                // A stop that changed nothing leaves the previous state true:
                // a synced library stays "synced", not "incomplete". Only a
                // run that actually landed content degrades the badge.
                const stopChanged = (result.imported ?? 0) + (result.updated ?? 0) + (result.tracksCreated ?? 0) + (result.tracksUpdated ?? 0) > 0;
                if (stopChanged || syncStatus !== 'synced') dispatch(setSyncStatus(kept.length ? 'partial' : 'not_synced'));
                track('dj_import_canceled', {
                    app: 'rekordbox',
                    playlists_imported: result.imported ?? 0,
                    tracks_created: result.tracksCreated ?? 0,
                    tracks_updated: result.tracksUpdated ?? 0,
                    duration_ms: Date.now() - importStartedAt,
                });
                toast.info(formatDjImportStopped(result));
                return;
            }

            dispatch(setLastSyncTime(new Date().toISOString()));
            dispatch(setSyncStatus('synced'));
            dispatch(triggerPlaylistRefresh());

            // Reload imported playlists from database (force refresh after import)
            await loadImportedPlaylists(true);

            track('dj_playlists_imported', {
                app: 'rekordbox',
                mode: playlistKeys ? 'selected' : 'all',
                excluded_count: excludeKeys.length,
                imported: result.imported ?? 0,
                updated: result.updated ?? 0,
                tracks_created: result.tracksCreated ?? 0,
                tracks_updated: result.tracksUpdated ?? 0,
                from: opts.from || 'sidebar',
                duration_ms: Date.now() - importStartedAt,
            });
            if (!maybeNudgeAutoSync(result, opts.from, { firstSync: wasFirstSync })) {
                toast.success(formatDjImportSummary(result, { app: 'Rekordbox' }));
            }
        } catch (err) {
            console.error('Error importing from Rekordbox:', err);
            toast.error(`Import failed: ${errMsg(err)}`);
            // Playlists commit whole, so a failure can still leave complete
            // imports behind — keep them visible; the badge says incomplete.
            const kept = await loadImportedPlaylists(true);
            dispatch(setSyncStatus(kept.length ? 'partial' : 'error'));
            dispatch(setRekordboxError(err.message));
        } finally {
            dispatch(setImporting(false));
        }
    };

    // "Sync now" from the toast card / OS notification opens this sidebar and
    // asks it to import once it's mounted.
    const [showSend, setShowSend] = useState(false);
    const [showImportPicker, setShowImportPicker] = useState(false);

    // Local playlists are owner-scoped (migration 012): on account switch or
    // session restore, drop the previous account's cached list and refetch.
    const prevUserRef = useRef(authUserId);
    useEffect(() => {
        if (prevUserRef.current === authUserId) return;
        prevUserRef.current = authUserId;
        dispatch(setRekordboxPlaylists([]));
        setPlaylistsLoaded(false);
        if (sidebarOpen) loadImportedPlaylists(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [authUserId]);
    const syncRequested = useSelector((state) => !!state.djLibraryChanges?.syncRequested?.rekordbox);
    const pendingDiff = useSelector((state) => state.djLibraryChanges?.pending?.rekordbox?.diff);
    useEffect(() => {
        if (!syncRequested) return;
        dispatch(clearSyncRequest('rekordbox'));
        // A change that includes never-imported playlists is a choice, not a
        // one-click action — open the picker (new rows start unchecked) so
        // hundreds of unimported playlists can't flood in behind one click.
        // Changes to already-synced playlists alone stay one-click.
        if (pendingDiff?.newPlaylists?.length) setShowImportPicker(true);
        else handleImport({ from: 'watcher_card' });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [syncRequested]);

    const handleExport = async () => {
        // Get all playlists from database
        let playlistsToExport = [];
        try {
            const allPlaylists = await window.electron.db.playlists.getAll(true);
            playlistsToExport = selectedPlaylistsForExport.length > 0
                ? allPlaylists.filter(p => selectedPlaylistsForExport.includes(p.id))
                : allPlaylists;
        } catch (err) {
            console.error('Error fetching playlists:', err);
            toast.error('Failed to fetch playlists');
            return;
        }

        if (playlistsToExport.length === 0) {
            toast.warning('No playlists to export');
            return;
        }

        const totalTracks = playlistsToExport.reduce((sum, p) => sum + (p.tracks?.length || 0), 0);

        // Show save dialog
        const result = await window.electron.dialog.showSaveDialog({
            title: 'Export playlists for Rekordbox',
            defaultPath: 'rekordbox-export.xml',
            filters: [{ name: 'XML Files', extensions: ['xml'] }]
        });

        if (result.canceled || !result.filePath) return;

        const confirmed = await toast.confirm(
            `This will export ${playlistsToExport.length} playlist${playlistsToExport.length !== 1 ? 's' : ''} with ${totalTracks} track${totalTracks !== 1 ? 's' : ''} to:\n\n${result.filePath}\n\nThis creates a NEW file. Your existing Rekordbox library will NOT be modified.\n\nTo use in Rekordbox:\nFile > Library > Import Playlist > Select the exported file`,
            'Export to Rekordbox',
            { confirmText: 'Export', confirmVariant: 'primary' }
        );

        if (!confirmed) return;

        dispatch(setExporting(true));
        try {
            await window.electron.rekordbox.exportPlaylists(playlistsToExport, result.filePath);
            toast.success(`Exported ${playlistsToExport.length} playlist${playlistsToExport.length !== 1 ? 's' : ''} to ${result.filePath}`);
            setSelectedPlaylistsForExport([]);
        } catch (err) {
            console.error('Error exporting playlists:', err);
            toast.error(`Export failed: ${errMsg(err)}`);
        } finally {
            dispatch(setExporting(false));
        }
    };

    const togglePlaylistSelection = (playlistId) => {
        setSelectedPlaylistsForExport(prev =>
            prev.includes(playlistId)
                ? prev.filter(id => id !== playlistId)
                : [...prev, playlistId]
        );
    };

    const formatSyncTime = (isoString) => {
        if (!isoString) return 'Never';
        const date = new Date(isoString);
        // Persisted "null"/garbage (fresh profiles) must not show "Invalid Date".
        return isNaN(date.getTime()) ? 'Never' : date.toLocaleString();
    };

    if (!sidebarOpen) {
        return null;
    }

    // Which imported playlists the user excluded from sync — kept locally by
    // design, skipped by re-syncs. localStorage read per render is cheap; the
    // Manage-imports picker stays the source of truth.
    const excludedKeySet = new Set(getImportExclusions('rekordbox', authUserId));
    const isExcludedFromSync = (p) => { const k = localPlaylistSelectionKey('rekordbox', p); return !!k && excludedKeySet.has(k); };

    return (
        <div
            ref={sidebarRef}
            className={`rekordbox-sidebar${isResizing ? ' resizing' : ''}`}
            style={{ width: `${sidebarWidth}px` }}
        >
            <SendToAppModal app="rekordbox" show={showSend} onClose={() => setShowSend(false)} onExportXml={handleExport} onSent={() => loadImportedPlaylists?.(true)} />
            <SyncFromAppModal app="rekordbox" show={showImportPicker} onClose={() => setShowImportPicker(false)} sourcePath={rekordboxPath} userId={authUserId} onImport={(keys) => handleImport({ playlistKeys: keys, from: 'picker' })} />
            <div className="sidebar-header">
                <div className="header-title">
                    <h4>Rekordbox Sync</h4>
                    <button
                        className="sidebar-collapse-btn"
                        onClick={() => dispatch(toggleRekordboxSidebar())}
                        title="Hide Rekordbox"
                    >
                        ‹
                    </button>
                </div>

                <div className="sync-status">
                    {syncStatus === 'checking' && (
                        <Badge bg="secondary">
                            <Spinner animation="border" size="sm" /> Checking...
                        </Badge>
                    )}
                    {syncStatus === 'synced' && (
                        <Badge bg="success">Playlists imported</Badge>
                    )}
                    {/* Stopped or failed mid-import: whole playlists are in
                        (each commits with its tracks) but the mirror isn't
                        complete yet — neither "imported" nor "no playlists"
                        is true, so say the third thing. */}
                    {syncStatus === 'partial' && <Badge bg="warning" text="dark">Sync incomplete</Badge>}
                    {/* "Not Synced" measured PLAYLISTS but sat beside a track
                        count, so a library with thousands of tracks imported
                        read as not synced at all — two true statements that
                        looked like a contradiction. Say which one this is. */}
                    {syncStatus === 'not_synced' && rekordboxExists && (
                        <Badge bg="secondary">No playlists imported</Badge>
                    )}
                    {syncStatus === 'not_synced' && !rekordboxExists && (
                        <Badge bg="secondary">No XML Found</Badge>
                    )}
                    {syncStatus === 'error' && (
                        <Badge bg="danger">Error</Badge>
                    )}
                </div>
            </div>

            <div className="sidebar-content">
                {(syncStatus === 'synced' || syncStatus === 'partial') ? (
                    // SYNCED VIEW
                    <div className="synced-view">
                        <div className="sync-info">
                            <DjChangeBanner app="rekordbox" onSync={() => handleImport({ from: 'banner' })} syncing={importing} />
                            <p className="last-sync">
                                Last synced: {formatSyncTime(lastSyncTime)}
                            </p>
                            <p className="xml-path" title={rekordboxPath}>{wrappablePath(rekordboxPath)}
                            </p>
                        </div>

                        <AutoSyncToggle app="rekordbox" label="Rekordbox" />

                        <DjLibraryMeter app="rekordbox" exists={rekordboxExists} userId={authUserId} sourcePath={rekordboxPath} importing={importing} onManage={() => setShowImportPicker(true)} />

                        <div className="section">
                            <h5>Imported Playlists ({rekordboxPlaylists.length})</h5>
                            {rekordboxPlaylists.length > 0 ? (
                                <ListGroup variant="flush" className="playlist-list">
                                    {rekordboxPlaylists.map((playlist) => (
                                        <ListGroup.Item key={playlist.id} className="playlist-item">
                                            <span className="playlist-name">{playlist.name}</span>
                                            {isExcludedFromSync(playlist) && <span className="dj-not-syncing-tag">not syncing</span>}
                                            <Badge bg="secondary" className="track-count">
                                                {playlist.trackCount || 0} tracks
                                            </Badge>
                                        </ListGroup.Item>
                                    ))}
                                </ListGroup>
                            ) : (
                                <p className="empty-message">No playlists imported yet</p>
                            )}
                        </div>

                        <div className="action-buttons">
                            <Button
                                variant="outline-primary"
                                onClick={() => handleImport({ from: 'resync' })}
                                disabled={importing}
                                className="action-btn"
                            >
                                {importing ? (
                                    <>
                                        <Spinner animation="border" size="sm" /> Re-importing...
                                    </>
                                ) : (
                                    'Re-sync from Rekordbox'
                                )}
                            </Button>
                            <Button
                                variant="outline-secondary"
                                onClick={() => setShowImportPicker(true)}
                                disabled={importing}
                                className="action-btn"
                            >
                                Manage imports…
                            </Button>
                            <Button
                                variant="outline-secondary"
                                onClick={() => setShowSend(true)}
                                className="action-btn"
                            >
                                Send playlists to Rekordbox…
                            </Button>
                        </div>
                    </div>
                ) : (
                    // NOT SYNCED VIEW
                    <div className="not-synced-view">
                        {/* Rekordbox Detection */}
                        <div className="section detection-section">
                            <h5>Rekordbox Library</h5>
                            {rekordboxExists ? (
                                <div className="detected">
                                    <div className="status-icon success">&#10003;</div>
                                    <div className="status-text">
                                        <p className="status-label">
                                            {rekordboxType === 'database' ? 'Rekordbox library found' : 'XML file found'}
                                        </p>
                                        <p className="xml-path" title={rekordboxPath}>{wrappablePath(rekordboxPath)}
                                        </p>
                                    </div>
                                </div>
                            ) : (
                                <div className="not-detected">
                                    <div className="status-icon warning">!</div>
                                    <div className="status-text">
                                        <p className="status-label">Rekordbox not found</p>
                                        <p className="hint">Make sure Rekordbox is installed, or select an exported XML file</p>
                                    </div>
                                </div>
                            )}
                            <div className="detection-actions">
                                {!rekordboxExists ? (
                                    <>
                                        {/* Recovery for FAILED detection. */}
                                        <Button
                                            variant="link"
                                            size="sm"
                                            onClick={rescanRekordbox}
                                            className="refresh-btn"
                                            title="Search the usual locations for your Rekordbox library again"
                                        >
                                            Re-scan
                                        </Button>
                                        <Button
                                            variant="outline-secondary"
                                            size="sm"
                                            onClick={handleSelectXmlFile}
                                            className="select-xml-btn"
                                            title="Use a library exported from Rekordbox (File → Export Collection in xml format)"
                                        >
                                            Select XML File
                                        </Button>
                                    </>
                                ) : (
                                    /* Same quiet link as the other DJ sidebars. Rekordbox's
                                       live DB is located via Pioneer's own pointer (moved
                                       libraries follow automatically), so "change location"
                                       here means: use an exported XML instead. */
                                    <button
                                        type="button"
                                        className="dj-location-change"
                                        onClick={handleSelectXmlFile}
                                        title="Use an exported Rekordbox XML instead (File → Export Collection in xml format)"
                                    >
                                        Change location…
                                    </button>
                                )}
                            </div>
                        </div>

                        {/* Import Section */}
                        <div className="section import-section">
                            <h5>Import from Rekordbox</h5>
                            <div className="safety-warning">
                                <span className="warning-icon">&#9432;</span>
                                <span>
                                    {rekordboxType === 'database'
                                        ? 'Reads directly from Rekordbox database. Your Rekordbox library will NOT be modified.'
                                        : 'Your Rekordbox library will NOT be modified. Playlists will be imported into MixGoat.'}
                                </span>
                            </div>

                            <DjLibraryMeter compact app="rekordbox" exists={rekordboxExists} userId={authUserId} sourcePath={rekordboxPath} />

                            <Button
                                variant="primary"
                                onClick={() => setShowImportPicker(true)}
                                disabled={!rekordboxExists || importing}
                                className="action-btn import-btn"
                            >
                                {importing ? (
                                    <>
                                        <Spinner animation="border" size="sm" /> Importing...
                                    </>
                                ) : (
                                    'Import from Rekordbox'
                                )}
                            </Button>
                        </div>

                        {/* Export Section */}
                        <div className="section export-section">
                            <h5>Send to Rekordbox</h5>
                            <p className="section-hint">Put MixGoat playlists into your Rekordbox library — pick which ones, only local files are sent.</p>
                            <Button variant="secondary" onClick={() => setShowSend(true)} className="action-btn export-btn">
                                Send playlists to Rekordbox…
                            </Button>
                        </div>

                    </div>
                )}
                {/* Both views: a failed re-sync must not hide the imported
                    playlists it didn't touch. */}
                {error && (
                    <ErrorBanner
                        variant="inline"
                        error={error}
                        surface="rekordbox_sync"
                        onRetry={() => {
                            dispatch(setRekordboxError(null));
                            if (syncStatus === 'error' || syncStatus === 'partial') handleImport();
                            else checkRekordboxXml();
                        }}
                    />
                )}
            </div>

            <div
                className="sidebar-resizer"
                onMouseDown={() => setIsResizing(true)}
            />
        </div>
    );
};

export default RekordboxSidebar;
